/**
 * Referral code localStorage management utilities.
 * Handles saving and retrieving referral codes from localStorage with expiration tracking.
 */

const REFERRAL_STORAGE_KEY = "ref_code";
const REFERRAL_EXPIRY_KEY = "ref_code_expiry";
const REFERRAL_EXPIRY_DAYS = 30;

/**
 * Set referral code in localStorage with 30-day expiration.
 * @param code - The referral code to save
 */
export function setReferralCode(code: string): void {
  if (typeof window === "undefined" || !window.localStorage) return;

  const expiryDate = new Date();
  expiryDate.setTime(expiryDate.getTime() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, code);
    localStorage.setItem(REFERRAL_EXPIRY_KEY, expiryDate.toISOString());
  } catch (e) {
    // Handle localStorage quota exceeded or other errors
    console.error("Failed to save referral code:", e);
  }
}

/**
 * Get referral code from localStorage.
 * @returns The referral code or null if not found or expired
 */
export function getReferralCode(): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;

  try {
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const expiry = localStorage.getItem(REFERRAL_EXPIRY_KEY);

    if (!code || !expiry) return null;

    // Check if expired
    const expiryDate = new Date(expiry);
    if (new Date() > expiryDate) {
      // Clean up expired data
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      localStorage.removeItem(REFERRAL_EXPIRY_KEY);
      return null;
    }

    return code;
  } catch (e) {
    // Handle localStorage access errors
    console.error("Failed to retrieve referral code:", e);
    return null;
  }
}

/**
 * Clear referral code from localStorage.
 */
export function clearReferralCode(): void {
  if (typeof window === "undefined" || !window.localStorage) return;

  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    localStorage.removeItem(REFERRAL_EXPIRY_KEY);
  } catch (e) {
    console.error("Failed to clear referral code:", e);
  }
}

/**
 * Check URL for referral parameter and save to localStorage if found.
 * Looks for ?ref=CODE in the URL parameters.
 */
export function checkAndSaveReferralCode(): void {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get("ref");

  if (refCode) {
    setReferralCode(refCode);
  }
}
